/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Thu May 04 2023
 **/

import React from "react";

import { Avatar, AvatarProps, Icon, Row, Typography, TypographyTooltip } from "@/frameworks/designStuff/components";
import { AvatarGroup } from "@/frameworks/designStuff/compounds";
import { Maybe } from "@/frameworks/libraries/components";
import { _T } from "@rfice/language";

import * as S from "./private/emotion";
import { useTheme } from "@emotion/react";

interface UserListItemProps extends React.HTMLAttributes<HTMLDivElement> {
	title: string;
	avatar?: Partial<AvatarProps>;
	avatarList?: AvatarProps[];
	typoSize?: React.ComponentProps<typeof Typography>["typoSize"];
	private?: boolean;
	isChatFeature?: boolean;
	isMe?: boolean;
	activeBackground?: boolean;
}

const ELLIPSIS_OFFSET = 60;

export default function UserListItem({
	title,
	typoSize = "Body3_MD",
	children,
	avatarList = [],
	isChatFeature = false,
	isMe = false,
	...props
}: UserListItemProps) {
	const theme = useTheme();
	return (
		<S.ShortDirectMessageWrap verticalAlign="center" {...props} horizonAlign="distribute" theme={theme}>
			<Row gap={3} style={{ maxWidth: `calc( 100% - ${ELLIPSIS_OFFSET}px )` }} verticalAlign="center">
				{avatarList?.length > 1 ? (
					<AvatarGroup
						size={isChatFeature ? "32px" : "24px"}
						avatarList={avatarList.map((avatar, index) => (
							<Avatar key={index} {...avatar} status={undefined} />
						))}
					/>
				) : (
					<Maybe test={avatarList.length > 0}>
						<Avatar {...avatarList[0]} />
					</Maybe>
				)}
				<TypographyTooltip.p
					tooltipProps={{ hasIcon: false }}
					componentProps={{
						align: "left",
						maxLine: 1,
						ellipsis: true,
						stretch: true,
						typoSize: typoSize,
						style: {
							paddingLeft: 9
						}
					}}
				>
					{isMe ? title + _T("(나)") : title}
				</TypographyTooltip.p>
				{props.private && <Icon size={16} css={{ minWidth: 16 }} name="Lock24" />}
			</Row>
			{children}
		</S.ShortDirectMessageWrap>
	);
}
